import { SettleResult, SubmitFn, isNavigatingResult, settle } from './settle'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { useHistory } from 'react-router-dom'
import logo from '../assets/img/blik.png'
import Popup from 'components/popups/Popup'
import React, { useRef, useState } from 'react'
import PinForm from 'components/PinForm'
import { STATUS } from 'utils/constants'

interface BlikPopupProps {
  /** buduje body rozliczenia; `extra` to { code } */
  getBody: (extra: Record<string, unknown>) => any
  /** wywołane raz: z wynikiem rozliczenia albo bez niczego, gdy klient zamknął popup */
  onResult: (result?: SettleResult) => void
  /** inny endpoint niż rozliczenie (np. dodanie metody w Koncie) - domyślnie `settle` */
  submit?: SubmitFn
  /** etykieta przycisku zamiast „Zapłać” */
  submitText?: string
  /** dodatkowa informacja pod opisem, np. o zwracanej kwocie weryfikacyjnej */
  note?: React.ReactNode
  /** zamknięcie popupu, gdy nie siedzi we własnym wpisie historii - domyślnie goBack() */
  close?: () => void
}

const BlikPopup: React.FC<BlikPopupProps> = ({ onResult, getBody, submit = settle, submitText, note, close }) => {
  const [status, setStatus] = useState<string>(STATUS.idle)
  const [isLoading, setLoading] = useState(false)
  const [warning, setWarning] = useState('')
  const [code, setCode] = useState('')
  const isActive = useRef(true)

  const { t } = useTranslation('payment')
  const { goBack } = useHistory()
  const closePopup = close || goBack

  const hidePopup = () => {
    isActive.current = false
    onResult()
    closePopup()
  }

  const send = async () => {
    if (isLoading || status === STATUS.pending || code.length !== 6) return
    const body = await getBody({ code })
    if (!body) return

    setLoading(true)
    setStatus(STATUS.idle)

    const result = await submit(body, {
      isActive: () => isActive.current,
      onPending: () => setStatus(STATUS.pending)
    })
    if (!isActive.current) return
    setLoading(false)

    if (result.type === 'error' || result.type === 'failure') {
      setStatus(STATUS.failed)
      setCode('')
      setWarning(result.handled ? '' : (result.message || ''))
      return
    }

    isActive.current = false
    onResult(result)
    if (!isNavigatingResult(result)) closePopup()
  }

  return (
    <Popup
      centered
      close={hidePopup}>
      <div className='popup__blik'>
        {status === STATUS.pending ? <>
          <div className='blik__title'>
            <i className='icon icon--mobile-hand s64' />
            <div>{t('confirmPayment')}</div>
          </div>
          <p className='--center'>{t('confirmPaymentInBankApp')}</p>
        </>
          : <>
            <div className='blik__title'>
              <img src={logo} alt='blik' />
              <div>{t('enterBlikCode')}</div>
            </div>
            <p>
              <span>{t('authorizationCodeSixDigitsInBankApp')}</span>
              {status === STATUS.failed
                ? <span className='--warning'> - {warning || t('wrongCode')}!</span>
                : '.'}
            </p>
            {note && <p className='popup__note'>{note}</p>}
            <div className='pin__form__container'>
              <PinForm
                isCode
                value={code}
                secondaryTheme
                setValue={setCode}
                warning={false}
              />
            </div>
          </>}

        {status !== STATUS.pending
          ? <div className='popup__buttons'>
            <ArrowBtn
              enlarged
              text={submitText || t('pay')}
              isLoading={isLoading}
              disabled={code.length !== 6}
              action={send} />
            <BackBtn
              text={t('cancel')}
              action={hidePopup} />
          </div>
          : <ArrowBtn
            enlarged
            text={t('cancel')}
            className='--black'
            action={hidePopup} />}
      </div>
    </Popup>
  )
}

export default BlikPopup
